import { useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

/**
 * The booking steps build on Redux state (station, travelers, seats) that
 * does not survive a page reload. Instead of rendering with missing state -
 * which crashes or shows a broken page - this sends the visitor back to the
 * earliest step that can rebuild what is missing, for the same train. Train
 * data itself is refetched by id, so the station and travelers steps work as
 * reload-safe entry points.
 *
 * Returns true when all prerequisites for the step are present; while false,
 * the step should render nothing (a redirect is already underway).
 *
 * Normal forward navigation is unaffected: the state is always present then.
 *
 * @param {'seats'|'book'} step
 */
export default function useBookingStepGuard(step) {
    const { id } = useParams();
    const history = useHistory();
    const activeStation = useSelector(state => state.activeStation);
    const travelers = useSelector(state => state.travelers);
    const { seats } = useSelector(state => state.activeSeats);

    const hasStation = !!activeStation;
    const hasTravelers = !!(travelers && travelers.total > 0);
    const hasSeats = !!(seats && seats.length > 0);

    let redirectTo = null;
    if (!hasStation) {
        redirectTo = `/trains/${id}/station`;
    } else if (!hasTravelers) {
        redirectTo = `/trains/${id}/travelers`;
    } else if (step === 'book' && !hasSeats) {
        redirectTo = `/trains/${id}/seats`;
    }

    useEffect(() => {
        if (redirectTo) {
            history.replace(redirectTo);
        }
    }, [redirectTo, history]);

    return redirectTo === null;
}
